import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Radar, ShieldCheck, ShieldAlert, ShieldX, ScanSearch, Scale } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useRegion, getRegionLabel } from '../context/RegionContext';
import { useAuth } from '../context/AuthContext';
import QuickCheckForm from '../components/scout/QuickCheckForm';
import BulkScanCard from '../components/scout/BulkScanCard';
import ScansTable from '../components/scout/ScansTable';
import VendorTable from '../components/scout/VendorTable';
import { getScoutScans, getScoutVendors } from '../services/scoutApi';

const STAT_TILES = [
  { key: 'total', label: 'Scanned', icon: ScanSearch, tone: 'text-foreground', filter: 'all' },
  { key: 'clean', label: 'Clean', icon: ShieldCheck, tone: 'text-emerald-600 dark:text-emerald-400', filter: 'clean' },
  { key: 'review', label: 'Review', icon: ShieldAlert, tone: 'text-amber-600 dark:text-amber-400', filter: 'review' },
  { key: 'high_risk', label: 'High Risk', icon: ShieldX, tone: 'text-red-600 dark:text-red-400', filter: 'high_risk' },
];

const StatTile = ({ tile, value, active, onClick }) => {
  const Icon = tile.icon;
  return (
    <button onClick={onClick} data-testid={`scout-stat-${tile.key}`}
      className={`rounded-2xl border bg-card px-4 py-3.5 text-left transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md ${active ? 'ring-2 ring-primary/40' : ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{tile.label}</span>
        <Icon className={`h-4 w-4 ${tile.tone}`} />
      </div>
      <p className={`mt-1 text-2xl font-extrabold tabular-nums ${tile.tone}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{value ?? 0}</p>
    </button>
  );
};

export default function ScoutPage() {
  const { region } = useRegion();
  const { user } = useAuth();
  const readOnly = user?.role === 'viewer';
  const [data, setData] = useState({ scans: [], stats: {} });
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await getScoutScans({ region, verdict: filter === 'all' ? undefined : filter, q });
      setData(r);
      clearTimeout(timer.current);
      if (r.stats?.running) timer.current = setTimeout(load, 4000);
    } catch { /* toast handled by actions */ }
  }, [region, filter, q]);

  useEffect(() => { const id = setTimeout(load, q ? 300 : 0); return () => { clearTimeout(id); clearTimeout(timer.current); }; }, [load, q]);
  useEffect(() => { getScoutVendors(region).then((r) => setVendors(r.vendors)).catch(() => {}); }, [region, data.stats?.total, data.stats?.running]);

  return (
    <div className="space-y-6" data-testid="scout-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-start gap-4">
          <span className="relative w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center shadow-lg shadow-indigo-900/20">
            <Radar className="h-6 w-6" />
            <span className="absolute inset-0 rounded-2xl border-2 border-indigo-400/60 animate-ping opacity-30" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Scout</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Catch fraudulent applicants before your team spends a minute on them · {getRegionLabel(region)}</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground bg-muted px-3 py-1.5 rounded-full" data-testid="scout-compliance-pill">
          <Scale className="h-3.5 w-3.5" /> Signals for human review — not a background check
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {STAT_TILES.map((t) => <StatTile key={t.key} tile={t} value={data.stats?.[t.key]} active={filter === t.filter && t.filter !== 'all'} onClick={() => setFilter(t.filter)} />)}
      </div>

      <div className={`grid gap-6 items-start ${readOnly ? '' : 'lg:grid-cols-[400px_1fr]'}`}>
        {!readOnly && (
          <div className="space-y-4">
            <QuickCheckForm region={region} onStarted={load} />
            <BulkScanCard region={region} onStarted={load} />
          </div>
        )}
        <Tabs defaultValue="scans">
          <TabsList>
            <TabsTrigger value="scans" data-testid="scout-tab-scans">Recent scans</TabsTrigger>
            <TabsTrigger value="vendors" data-testid="scout-tab-vendors">Vendors</TabsTrigger>
          </TabsList>
          <TabsContent value="scans" className="mt-3"><ScansTable scans={data.scans} filter={filter} onFilter={setFilter} q={q} onSearch={setQ} /></TabsContent>
          <TabsContent value="vendors" className="mt-3"><VendorTable vendors={vendors} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
